import AvatarTimer from '@/components/ANewDesign/PlayerCell/AvatarTimer'
import { PlayerRole, type PlayerRole as PlayerRoleType } from '@/components/ANewDesign/PlayerRole'
import { PokerCard } from '@/components/GameAssets/Cards/PokerCard'
import { DS } from '@/design-system'
import { useResponsive } from '@/screens/new-design/hooks/useResponsive'
import { Suit, Value } from 'pokersolver'
import { useMemo } from 'react'
import { FlexAlignType, View, ViewStyle } from 'react-native'
import { createComponent } from 'react-native-small-ui'
import { translate } from '../../../i18n'
import { numberWithCommas } from '../../../utils/numberWithCommas'
import type { BotSkillLevel } from '@ppwr/poker-engine/utils'

const Box = createComponent(View, {
  flexGrow: 1
})
const Row = createComponent(View, {
  flexDirection: 'row'
})

type FaceUpCards = [0 | 1, 0 | 1]

type VideoConfig = {
  playerId: number
}

type PlayerBoxProps = {
  name: string
  state: string
  action?: PlayerAction
  //
  cards?: string[]
  faceUpCards?: FaceUpCards
  //
  chips?: number
  position?: 'd' | 'sb' | 'bb'
  //
  alignment?: Extract<FlexAlignType, 'flex-end' | 'flex-start' | 'center'>
  containerStyle?: ViewStyle
  timerProps: Pick<React.ComponentProps<typeof AvatarTimer>, 'timeMS' | 'enable' | 'startTime'>
  video?: VideoConfig
  avatarTextColor?: string
  avatarBgColor?: string
  userId?: string
  kind?: 'human' | 'bot'
  skillLevel?: BotSkillLevel
}

export const PlayerBox = (props: PlayerBoxProps) => {
  return (
    <Box
      //
      gap={2}
      paddingHorizontal={0}
      alignItems={props.alignment === 'center' ? 'center' : undefined}
      style={props?.containerStyle}
    >
      {/* Player Avatar & Cards */}
      <Row gap={0} flexDirection={props.alignment === 'flex-end' ? 'row-reverse' : 'row'}>
        <AvatarTimer
          {...props.timerProps}
          avatarProps={{
            name: props.name,
            video: props.video,
            textColor: props.avatarTextColor,
            backgroundColor: props.avatarBgColor
          }}
          kind={props.kind}
          userId={props.userId}
          skillLevel={props.skillLevel}
          containerStyle={{
            width: 'auto',
            height: 'auto',
            transform: [{ scale: 0.85 }]
          }}
        />
        {props.cards && <PlayerCards cards={props.cards} faceUp={props.faceUpCards} />}
      </Row>

      {/* Player Name */}
      <DS.TextScaled
        variant="Caption/Bold"
        style={{
          alignSelf: props.alignment
        }}
        numberOfLines={1}
        color={'Brand/Cream'}
      >
        {props.name}
      </DS.TextScaled>
      {/* Player Role and Chips */}
      <Row
        gap={4}
        minHeight={22}
        //
        flexDirection={props.alignment === 'flex-end' ? 'row-reverse' : 'row'}
      >
        <PokerPosition position={props?.position} />
        <Chips amount={props?.chips || 0} />
      </Row>
      {/* CurrentAction */}
      <Row minHeight={20} justifyContent={props.alignment}>
        {props.action && <Action currentAction={props.action} playerState={props.state} />}
      </Row>
    </Box>
  )
}

type PlayerAction = { amount?: number; action: string }
type ActionTextFunction = (playerAction: PlayerAction, playerState: string) => string

const getActionText: ActionTextFunction = ({ amount, action }, playerState) => {
  let actionText = ''
  if (!action) {
    return actionText
  }

  // for player state
  try {
    // TODO failed to find >> PlayerBox.getActionText playerState: active [Error: missing translation "newGameScreen.player.lastAction.active"]
    const i18Key = `newGameScreen.player.lastAction.${playerState}`
    const translation = translate(i18Key)
    if (translation.startsWith('[missing') && translation.endsWith('translation]')) {
      throw new Error(`missing translation "${i18Key}"`)
    }
    actionText = translation
  } catch (error) {
    console.warn('PlayerBox.getActionText playerState:', playerState, error)
  }
  try {
    // for Action
    const i18Key = `newGameScreen.player.lastAction.${action.toUpperCase()}`
    const translation = translate(i18Key)
    if (translation.startsWith('[missing') && translation.endsWith('translation]')) {
      throw new Error(`missing translation "${i18Key}"`)
    }
    actionText = translation
  } catch (error) {
    console.warn('PlayerBox.getActionText action:', action, error)
  }

  if (amount && amount > 0) {
    actionText += ` ${numberWithCommas(amount)}`
  }
  return actionText
}

const Action = ({ currentAction, playerState }: { currentAction: PlayerAction; playerState: string }) => {
  const actionText = useMemo(() => {
    return getActionText(currentAction, playerState)
  }, [currentAction, playerState])

  if (actionText === '') {
    return null
  }

  return (
    <ActionContainer>
      <DS.TextScaled
        variant={'Mini/Bold'}
        color={'Global/Black'}
        uppercase
        style={{
          width: '100%',
          paddingHorizontal: 5,
          paddingVertical: 4
        }}
      >
        {actionText}
      </DS.TextScaled>
    </ActionContainer>
  )
}

const ActionContainer = createComponent(View, {
  maxHeight: 20,
  borderTopLeftRadius: 2,
  borderTopRightRadius: 12,
  borderBottomLeftRadius: 12,
  borderBottomRightRadius: 12,
  backgroundColor: DS.palette['Brand/Light Grey']
})

const PokerPosition = ({ position }: Pick<PlayerBoxProps, 'position'>) => {
  if (!position) {
    return null
  }
  return <PlayerRole role={position.toUpperCase() as PlayerRoleType} />
}

const NumberFormatterRegex = /\B(?=(\d{3})+(?!\d))/g
export const formatChipsNumber = (number: string | number) => {
  return number.toString().replace(NumberFormatterRegex, ',')
}

const Chips = ({ amount }: { amount: number }) => {
  return (
    <Row gap={3} alignSelf="center">
      <DS.Frame
        style={
          {
            //opacity: applyOpacity ? 0.4 : 1
          }
        }
      >
        <DS.Icon name="ChipOutline" color={DS.palette['Global/White']} size={12} />
      </DS.Frame>
      <DS.TextScaled
        style={
          {
            // opacity: applyOpacity ? 0.4 : 1,
          }
        }
        variant={'Mini/Bold'}
        color={'Global/White'}
      >
        {formatChipsNumber(amount ?? 0)}
      </DS.TextScaled>
    </Row>
  )
}

type PlayerCardsProps = {
  cards: string[]
  faceUp?: FaceUpCards
  containerStyle?: ViewStyle
}
const PlayerCards = ({ cards, faceUp = [0, 0], containerStyle }: PlayerCardsProps) => {
  const { verticalScale } = useResponsive()
  const cardWidth = verticalScale(22)

  const leftCard = useMemo(() => cards.at(0), [cards])
  const rightCard = useMemo(() => cards.at(1), [cards])

  return (
    <Row gap={2} style={containerStyle}>
      <PokerCard.Flip
        suit={leftCard?.[1] as Suit | undefined}
        value={leftCard?.[0] as Value | undefined}
        size={cardWidth}
        side={faceUp[0] === 1 && leftCard ? 'front' : 'back'}
      />
      <PokerCard.Flip
        suit={rightCard?.[1] as Suit | undefined}
        value={rightCard?.[0] as Value | undefined}
        size={cardWidth}
        side={faceUp[0] === 1 && rightCard ? 'front' : 'back'}
      />
    </Row>
  )
}
